/**
 * TestSendDialog Component
 * Dialog for sending a test email to verify email snippet content
 * Allows entering a test email address and customizing variable values
 */

import { useState, useMemo, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Send,
  Mail,
  Braces,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Info,
} from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'

// ============================================
// Types
// ============================================

export interface TestSendDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  subject?: string
  body: string
  signature?: string
  isHtml: boolean
  onSend?: (email: string, variableValues: Record<string, string>) => Promise<void>
}

interface VariableValue {
  name: string
  displayName: string
  value: string
}

// ============================================
// Default Variable Values
// ============================================

const DEFAULT_VARIABLE_VALUES: Record<string, string> = {
  userName: 'John Smith',
  userFirstName: 'John',
  userLastName: 'Smith',
  userEmail: 'john.smith@company.com',
  userTitle: 'Senior Manager',
  userDepartment: 'Marketing',
  companyName: 'ACME Corporation',
  companyAddress: '1 Example Street',
  companyCity: 'New York',
  companyCountry: 'United States',
  companyPhone: '+1 (555) 123-4567',
  companyEmail: 'info@acme.com',
  companyWebsite: 'www.acme.com',
  recipientName: 'Jane Doe',
  recipientFirstName: 'Jane',
  recipientLastName: 'Doe',
  recipientEmail: 'jane.doe@client.com',
  recipientTitle: 'Director',
  recipientCompany: 'Client Corp',
  date: new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }),
  dateShort: new Date().toLocaleDateString('en-US'),
  time: new Date().toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }),
  year: new Date().getFullYear().toString(),
  month: new Date().toLocaleDateString('en-US', { month: 'long' }),
  day: new Date().getDate().toString(),
}

// ============================================
// Helper Functions
// ============================================

/**
 * Extract unique variable names from content
 */
function extractVariables(content: string): string[] {
  const regex = /\{\{(\w+)\}\}/g
  const matches: string[] = []
  let match: RegExpExecArray | null
  while ((match = regex.exec(content)) !== null) {
    const varName = match[1]
    if (varName && !matches.includes(varName)) {
      matches.push(varName)
    }
  }
  return matches
}

/**
 * Convert variable name to display name
 */
function toDisplayName(varName: string): string {
  return varName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim()
}

/**
 * Validate email format
 */
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

// ============================================
// TestSendDialog Component
// ============================================

export function TestSendDialog({
  open,
  onOpenChange,
  subject = '',
  body,
  signature = '',
  isHtml,
  onSend,
}: TestSendDialogProps) {
  const { toast } = useToast()
  const [testEmail, setTestEmail] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [sendResult, setSendResult] = useState<'success' | 'error' | null>(null)
  const [variableValues, setVariableValues] = useState<Record<string, string>>(
    () => ({ ...DEFAULT_VARIABLE_VALUES })
  )

  // Extract all variables from subject, body, and signature
  const allVariables = useMemo(() => {
    const vars: VariableValue[] = []
    const seen = new Set<string>()

    const addVars = (content: string) => {
      extractVariables(content).forEach((name) => {
        if (!seen.has(name)) {
          seen.add(name)
          vars.push({
            name,
            displayName: toDisplayName(name),
            value: variableValues[name] || '',
          })
        }
      })
    }

    addVars(subject)
    addVars(body)
    if (signature) addVars(signature)

    return vars
  }, [subject, body, signature, variableValues])

  // Update variable value
  const updateVariableValue = useCallback(
    (name: string, value: string) => {
      setVariableValues((prev) => ({ ...prev, [name]: value }))
    },
    []
  )

  // Handle send
  const handleSend = useCallback(async () => {
    if (!isValidEmail(testEmail)) {
      toast({
        title: 'Invalid email',
        description: 'Please enter a valid email address',
        variant: 'destructive',
      })
      return
    }

    setIsSending(true)
    setSendResult(null)

    try {
      if (onSend) {
        await onSend(testEmail, variableValues)
      } else {
        // Simulate sending for demo purposes
        await new Promise((resolve) => setTimeout(resolve, 1500))
      }

      setSendResult('success')
      toast({
        title: 'Email sent',
        description: `Test email sent to ${testEmail}`,
      })

      // Close dialog after success
      setTimeout(() => {
        onOpenChange(false)
        setSendResult(null)
        setTestEmail('')
      }, 1500)
    } catch (error) {
      setSendResult('error')
      toast({
        title: 'Failed to send',
        description: 'Failed to send test email. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsSending(false)
    }
  }, [testEmail, variableValues, onSend, onOpenChange, toast])

  // Reset state when dialog closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSendResult(null)
      setIsSending(false)
    }
    onOpenChange(open)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-rose-400 to-rose-600">
              <Send className="h-5 w-5 text-white" />
            </div>
            <div>
              <DialogTitle>Send Test Email</DialogTitle>
              <DialogDescription>
                Send a test email to verify your email snippet looks correct
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Test Email Address */}
          <div className="space-y-2">
            <Label htmlFor="test-email" className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-neutral-500" />
              Send to Email Address
            </Label>
            <Input
              id="test-email"
              type="email"
              placeholder="your.email@example.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              disabled={isSending}
              className="rounded-xl"
            />
          </div>

          {/* Variable Values */}
          {allVariables.length > 0 && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Braces className="h-4 w-4 text-blue-500" />
                Variable Values ({allVariables.length})
              </Label>
              <ScrollArea className="max-h-[200px] rounded-xl border border-neutral-200 dark:border-neutral-700">
                <div className="p-3 space-y-3">
                  {allVariables.map((variable) => (
                    <div key={variable.name} className="space-y-1">
                      <Label
                        htmlFor={`var-${variable.name}`}
                        className="text-xs text-neutral-500 dark:text-neutral-400"
                      >
                        {variable.displayName}{' '}
                        <code className="text-blue-500">{`{{${variable.name}}}`}</code>
                      </Label>
                      <Input
                        id={`var-${variable.name}`}
                        type="text"
                        value={variableValues[variable.name] || ''}
                        onChange={(e) =>
                          updateVariableValue(variable.name, e.target.value)
                        }
                        placeholder={`Enter ${variable.displayName.toLowerCase()}`}
                        disabled={isSending}
                        className="h-8 text-sm rounded-lg"
                      />
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Info Alert */}
          <Alert className="rounded-xl bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
            <Info className="h-4 w-4 text-blue-500" />
            <AlertDescription className="text-sm text-blue-700 dark:text-blue-300">
              The test email will use the variable values specified above.
              {isHtml
                ? ' The email will be sent as HTML.'
                : ' The email will be sent as plain text.'}
            </AlertDescription>
          </Alert>

          {/* Result Status */}
          {sendResult === 'success' && (
            <Alert className="rounded-xl bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <AlertDescription className="text-sm text-green-700 dark:text-green-300">
                Test email sent successfully to {testEmail}
              </AlertDescription>
            </Alert>
          )}

          {sendResult === 'error' && (
            <Alert className="rounded-xl bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800">
              <AlertCircle className="h-4 w-4 text-red-500" />
              <AlertDescription className="text-sm text-red-700 dark:text-red-300">
                Failed to send test email. Please try again.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSending}
            className="rounded-xl"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={isSending || !testEmail || sendResult === 'success'}
            className="rounded-xl bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700"
          >
            {isSending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : sendResult === 'success' ? (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Sent!
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Test Email
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default TestSendDialog
